import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"

// Editing for the capacity-planning timeline (Zeitstrahl) layout: move a duration bar, resize its
// ends, or remove it. The bars are derived from daily allocations; every edit POSTs the run's new
// [from, to] (with its original extent) to set_span / clear_span, which reconciles the daily rows
// and re-renders the affected site section. Drag-CREATE of a new bar reuses the site-duration drop
// flow on the section (handled by planning-board-drop), so it isn't repeated here.
//
// In aggregated views (week/month/quarter) edits SNAP to whole period boundaries, so dragging in
// the 3-year / quarter view lands predictably on a quarter edge. The bar redraws live and its PT
// label updates as you drag; the server re-render on release is authoritative.
export default class extends Controller {
  static values = {
    setSpanUrl: String,
    clearSpanUrl: String,
    // The employee lanes' multi-span dialog and the trade lanes' count edit (a fleet lane carries
    // its own set_spans URL on its controls).
    setSpansUrl: String,
    setCountUrl: String,
    periods: Array,
  }

  connect() {
    this.drag = null
    // A period spanning more than one day means the columns are aggregated (week/month/quarter).
    this.aggregated = this.periodsValue.some((p) => p.start !== p.end)
    this.boundMove = this.onPointerMove.bind(this)
    this.boundUp = this.onPointerUp.bind(this)
  }

  disconnect() {
    this.endPointerTracking()
  }

  // One pointer handler for every bar on the board instead of an action per bar and handle: a
  // press on an editable bar moves it, a press on one of its end handles resizes that end.
  pointerdown(event) {
    const bar = event.target.closest("[data-planning-timeline-target='bar']")
    if (!bar || bar.dataset.timelineEditable !== "true") return
    const handle = event.target.closest("[data-timeline-resize]")
    this.begin(event, handle ? `resize-${handle.dataset.timelineResize}` : "move")
  }

  // The lane labels' edit buttons, delegated for the same reason.
  click(event) {
    if (event.target.closest("[data-timeline-edit-spans]")) this.editSpans(event)
    else if (event.target.closest("[data-timeline-edit-count]")) this.editTradeCount(event)
  }

  // Common pointer-down setup. `mode` is move / resize-left / resize-right. The handle elements
  // sit inside the bar, so resolve the bar (and its track + label) from the event target.
  begin(event, mode) {
    // The edit / remove buttons live inside the bar; a press on them must not start a drag.
    if (event.target.closest("button")) return
    const bar = event.target.closest("[data-planning-timeline-target='bar']")
    if (!bar) return
    const track = bar.parentElement
    if (!track) return

    event.preventDefault()
    event.stopPropagation()

    const label = bar.querySelector("[data-timeline-label]")
    this.drag = {
      mode,
      bar,
      track,
      label,
      // The bar's pre-drag geometry/label, so a failed save can snap the optimistic preview back.
      originLeft: bar.style.left,
      originWidth: bar.style.width,
      originLabel: label ? label.textContent : null,
      from: bar.dataset.spanFrom,
      to: bar.dataset.spanTo,
      downDate: this.xToDate(event.clientX, track),
      moved: false,
    }
    window.addEventListener("pointermove", this.boundMove)
    window.addEventListener("pointerup", this.boundUp)
  }

  onPointerMove(event) {
    if (!this.drag) return
    const next = this.computeRange(event.clientX)
    if (!next) return
    this.drag.moved = true
    this.drag.next = next
    this.previewBar(next.from, next.to)
  }

  async onPointerUp() {
    const drag = this.drag
    this.endPointerTracking()
    if (!drag) return
    // A press on the bar body without dragging is a click: edit that run in the Planung dialog.
    if (!drag.moved && drag.mode === "move") return this.openRunEdit(drag.bar)
    if (!drag.moved || !drag.next) return
    if (drag.next.from === drag.from && drag.next.to === drag.to) return

    // Fleet bars carry their own set_fleet_span URL (and post fleet_id); employee bars fall back to
    // the board-level employee set_span endpoint.
    const url = drag.bar.dataset.setSpanUrl || this.setSpanUrlValue
    const { ok } = await planningMutation({
      url,
      method: "PATCH",
      body: this.bodyFor(drag.bar, drag.next.from, drag.next.to),
      component: "planning-timeline",
    })
    // The bar was repositioned optimistically while dragging; if the save failed (and so no
    // re-render replaced it), snap it back to where it started so the view stays truthful.
    if (!ok) {
      drag.bar.style.left = drag.originLeft
      drag.bar.style.width = drag.originWidth
      if (drag.label && drag.originLabel !== null) drag.label.textContent = drag.originLabel
      this.toggleGaps(drag.bar, false)
    }
  }

  // Candidate [from, to] for the current pointer position. In aggregated views the dragged edge
  // snaps to a whole period boundary (quarter/month/week); in day view it stays day-precise.
  computeRange(clientX) {
    const drag = this.drag
    const pointer = this.xToDate(clientX, drag.track)
    if (drag.mode === "resize-left") {
      const from = this.minDate(this.snap(pointer, "start"), drag.to)
      return { from, to: drag.to }
    }
    if (drag.mode === "resize-right") {
      const to = this.maxDate(this.snap(pointer, "end"), drag.from)
      return { from: drag.from, to }
    }
    // move: shift both ends by the day delta between press and pointer, then snap the whole bar.
    const delta = this.daysBetween(drag.downDate, pointer)
    const from = this.addDays(drag.from, delta)
    const to = this.addDays(drag.to, delta)
    return { from: this.snap(from, "start"), to: this.snap(to, "end") }
  }

  // Open the multi-span edit dialog for the lane: lists ALL of the employee's runs on the plannable
  // (including ones scrolled out of the window) so they can be edited / removed / added together.
  // Removal of the whole lane is handled separately by the [data-planning-remove] confirm flow.
  editSpans(event) {
    event.preventDefault()
    event.stopPropagation()
    const el = event.target.closest("[data-timeline-controls]")
    if (!el) return
    let spans
    try {
      spans = JSON.parse(el.dataset.spans || "[]")
    } catch {
      spans = []
    }
    document.dispatchEvent(
      new CustomEvent("planning:edit-spans", {
        detail: {
          setSpansUrl: el.dataset.setSpansUrl || this.setSpansUrlValue,
          plannableType: el.dataset.plannableType,
          plannableId: el.dataset.plannableId,
          plannableName: el.dataset.plannableName,
          // Employee lane: employeeId/Name. Fleet lane: resourceType "Fleet" + fleetId/Name.
          resourceType: el.dataset.resourceType,
          employeeId: el.dataset.employeeId,
          employeeName: el.dataset.employeeName,
          fleetId: el.dataset.fleetId,
          fleetName: el.dataset.fleetName,
          spans,
        },
      }),
    )
  }

  // "Einsatz bearbeiten" for an employee bar — the whole run: change its Zeitraum, or pick another
  // Baustelle/Aufgabe to move it there. Fleet and trade bars carry a resourceType and keep their
  // own editors (drag, multi-span dialog, count).
  openRunEdit(bar) {
    const data = bar.dataset
    if (data.resourceType || !data.employeeId) return
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "plannablePick",
          editMode: true,
          setSpanUrl: this.setSpanUrlValue,
          originalFrom: data.spanFrom,
          originalTo: data.spanTo,
          employeeId: Number(data.employeeId),
          employeeName: data.employeeName,
          plannableType: data.plannableType,
          plannableId: Number(data.plannableId),
          plannableName: data.plannableName,
          periodStart: data.spanFrom,
          periodEnd: data.spanTo,
          label: data.spanLabel || "",
        },
      }),
    )
  }

  // Request body shared by set_span / clear_span: which employee, which plannable, the new range,
  // and the bar's original extent so the server prunes only this run. working_days_only mirrors the
  // create flow so a resize never fills weekends/holidays.
  bodyFor(bar, from, to) {
    const body = {
      plannable_type: bar.dataset.plannableType,
      plannable_id: Number(bar.dataset.plannableId),
      date_from: from,
      date_to: to,
      original_from: bar.dataset.spanFrom,
      original_to: bar.dataset.spanTo,
      working_days_only: "1",
    }
    // Fleet bars identify the resource by fleet_id; trade bars by trade_id (+ the run's count, kept
    // through a resize/move); employee bars by employee_id.
    if (bar.dataset.resourceType === "Fleet") {
      body.fleet_id = Number(bar.dataset.fleetId)
    } else if (bar.dataset.resourceType === "Trade") {
      // Sent as is: an id, or "none" for a demand without a trade (Number() would turn that into null).
      body.trade_id = bar.dataset.tradeId
      body.required_count = Number(bar.dataset.requiredCount) || 1
    } else {
      body.employee_id = Number(bar.dataset.employeeId)
    }
    return body
  }

  // Trade-demand lane label: open the planning dialog to edit the whole lane's required count (the
  // date range is edited by dragging the bars). Reads the lane's identity + extent from the label's
  // controls element and opens the dialog in count-only mode; saving PATCHes set_count.
  editTradeCount(event) {
    event.preventDefault()
    event.stopPropagation()
    const el = event.target.closest("[data-timeline-trade-controls]")
    if (!el) return
    document.dispatchEvent(
      new CustomEvent("planning:plan-range", {
        detail: {
          mode: "fixed",
          editMode: true,
          editCount: true,
          resourceType: "Trade",
          resourceId: el.dataset.tradeId,
          resourceName: el.dataset.tradeName,
          setCountUrl: el.dataset.setCountUrl || this.setCountUrlValue,
          requiredCount: Number(el.dataset.requiredCount) || 1,
          plannableType: el.dataset.plannableType,
          plannableId: el.dataset.plannableId,
          plannableName: el.dataset.plannableName,
          periodStart: el.dataset.spanFrom,
          periodEnd: el.dataset.spanTo,
        },
      }),
    )
  }

  // Live left/width preview + PT label while dragging (mirrors SpanBarComponent's column math and
  // the working-day budget). The server re-render on pointer-up is authoritative.
  previewBar(from, to) {
    const n = this.periodsValue.length
    if (!n) return
    const left = this.dateToUnits(from, "start")
    const right = this.dateToUnits(to, "end")
    this.drag.bar.style.left = `${((left / n) * 100).toFixed(4)}%`
    this.drag.bar.style.width = `${(((right - left) / n) * 100).toFixed(4)}%`
    if (this.drag.label) this.drag.label.textContent = `${this.businessDays(from, to)} PT`
    this.toggleGaps(this.drag.bar, from !== this.drag.from || to !== this.drag.to)
  }

  // The weekend gaps inside a bar are positioned relative to its ORIGINAL extent: hide them while
  // the bar is reshaped (they'd stretch onto the wrong days), show them once it is back where it
  // started. The server re-render after a save redraws them for the new run.
  toggleGaps(bar, hidden) {
    bar.querySelectorAll("[data-timeline-gap]").forEach((gap) => (gap.hidden = hidden))
  }

  endPointerTracking() {
    window.removeEventListener("pointermove", this.boundMove)
    window.removeEventListener("pointerup", this.boundUp)
    this.drag = null
  }

  // --- granularity snapping ---

  // Snap a date to a whole period boundary in aggregated views: the period's start for the left
  // edge, its end for the right edge. Day view returns the date unchanged.
  snap(dateStr, edge) {
    if (!this.aggregated) return dateStr
    const period = this.periodForDate(dateStr)
    return edge === "end" ? period.end : period.start
  }

  periodForDate(dateStr) {
    const periods = this.periodsValue
    if (dateStr < periods[0].start) return periods[0]
    for (const period of periods) {
      if (dateStr >= period.start && dateStr <= period.end) return period
    }
    return periods[periods.length - 1]
  }

  // --- pixel ↔ date over the visible period columns ---

  xToDate(clientX, track) {
    const periods = this.periodsValue
    const n = periods.length
    const rect = track.getBoundingClientRect()
    const colWidth = rect.width / n
    const rel = Math.min(Math.max(clientX - rect.left, 0), rect.width - 1)
    const col = Math.min(Math.max(Math.floor(rel / colWidth), 0), n - 1)
    const within = (rel - col * colWidth) / colWidth
    const period = periods[col]
    const days = this.daysBetween(period.start, period.end) + 1
    const offset = Math.min(Math.max(Math.floor(within * days), 0), days - 1)
    return this.addDays(period.start, offset)
  }

  dateToUnits(dateStr, edge) {
    const periods = this.periodsValue
    for (let i = 0; i < periods.length; i++) {
      const period = periods[i]
      if (dateStr < period.start) return i
      if (dateStr <= period.end) {
        const days = this.daysBetween(period.start, period.end) + 1
        const offset = this.daysBetween(period.start, dateStr)
        const fraction = edge === "end" ? (offset + 1) / days : offset / days
        return i + fraction
      }
    }
    return periods.length
  }

  // --- date helpers (UTC, ISO 'YYYY-MM-DD' in/out) ---

  parse(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number)
    return Date.UTC(y, m - 1, d)
  }

  format(ms) {
    return new Date(ms).toISOString().slice(0, 10)
  }

  addDays(dateStr, days) {
    return this.format(this.parse(dateStr) + days * 86400000)
  }

  daysBetween(fromStr, toStr) {
    return Math.round((this.parse(toStr) - this.parse(fromStr)) / 86400000)
  }

  // Mon–Fri count in [from, to] inclusive (approximate PT — ignores holidays, which the server
  // applies on save). O(1): full weeks contribute 5 each, then the partial-week tail.
  businessDays(fromStr, toStr) {
    if (toStr < fromStr) return 0
    const total = this.daysBetween(fromStr, toStr) + 1
    const fullWeeks = Math.floor(total / 7)
    let count = fullWeeks * 5
    const startWday = new Date(this.parse(fromStr)).getUTCDay()
    for (let i = 0; i < total - fullWeeks * 7; i++) {
      const wday = (startWday + i) % 7
      if (wday !== 0 && wday !== 6) count++
    }
    return count
  }

  minDate(a, b) {
    return a < b ? a : b
  }

  maxDate(a, b) {
    return a > b ? a : b
  }
}
